import { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../../common/axiosInstance";
import { handleApiError } from "../../../../common/utils";

export interface ChecklistCategory {
  _id: string;
  categoryId: string;
  categoryName: string;
  createdAt?: string;
}

export interface ChecklistCategoryFormData {
  categoryId: string;
  categoryName: string;
  _id?: string;
  createdAt?: string;
}

interface UseChecklistCategoriesReturn {
  categories: ChecklistCategory[];
  loading: boolean;
  error: string | boolean;
  fetchCategories: () => Promise<boolean>;
  createCategory: (data: Partial<ChecklistCategoryFormData>) => Promise<void>;
  updateCategory: (
    id: string,
    data: Partial<ChecklistCategoryFormData>
  ) => Promise<boolean>;
  deleteCategory: (id: string) => Promise<void>;
  refreshCategories: () => void;
  clearError: () => void;
}

interface UseChecklistCategoriesOptions {
  autoFetch?: boolean;
  pollingInterval?: number;
  refreshOnFocus?: boolean;
}

export const useChecklistCategories = (
  options: UseChecklistCategoriesOptions = {}
): UseChecklistCategoriesReturn => {
  const {
    autoFetch = true,
    pollingInterval = 0,
    refreshOnFocus = false,
  } = options;

  const [categories, setCategories] = useState<ChecklistCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | boolean>(false);

  const fetchCategories = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(false);

    try {
      const response = await axiosInstance.get(`/api/checklistcategory`);

      const categoryData =
        response?.data?.results || response?.data?.data || [];
      setCategories(categoryData);
      setLoading(false);
      return true;
    } catch (error: any) {
      setLoading(false);
      const errorMessage = "Failed to fetch checklist categories";
      handleApiError(error, errorMessage);
      setError(errorMessage);
      return false;
    }
  }, []);

  const createCategory = useCallback(
    async (data: Partial<ChecklistCategoryFormData>): Promise<void> => {
      setLoading(true);
      setError(false);

      try {
        await axiosInstance.post(`/api/checklistcategory`, data);

        await fetchCategories();
      } catch (error: any) {
        setLoading(false);
        const errorMessage = "Failed to create checklist category";
        setError(errorMessage);
        handleApiError(error, errorMessage);
        throw error;
      }
    },
    [fetchCategories]
  );

  const updateCategory = useCallback(
    async (
      id: string,
      data: Partial<ChecklistCategoryFormData>
    ): Promise<boolean> => {
      setLoading(true);
      setError(false);

      try {
        await axiosInstance.put(`/api/checklistcategory/${id}`, data);

        await fetchCategories();
        return true;
      } catch (error: any) {
        setLoading(false);
        const errorMessage = "Failed to update checklist category";
        setError(errorMessage);
        handleApiError(error, errorMessage);
        return false;
      }
    },
    [fetchCategories]
  );

  const deleteCategory = useCallback(
    async (id: string): Promise<void> => {
      setLoading(true);
      setError(false);

      try {
        await axiosInstance.delete(`/api/checklistcategory/${id}`);

        await fetchCategories();
      } catch (error: any) {
        setLoading(false);
        const errorMessage = "Failed to delete checklist category";
        setError(errorMessage);
        handleApiError(error, errorMessage);
        throw error;
      }
    },
    [fetchCategories]
  );

  const refreshCategories = useCallback(() => {
    fetchCategories();
  }, [fetchCategories]);

  const clearError = useCallback(() => {
    setError(false);
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchCategories();
    }
  }, [autoFetch, fetchCategories]);

  useEffect(() => {
    if (pollingInterval > 0) {
      const intervalId = setInterval(() => {
        refreshCategories();
      }, pollingInterval);

      return () => clearInterval(intervalId);
    }
  }, [pollingInterval, refreshCategories]);

  useEffect(() => {
    if (refreshOnFocus) {
      const handleFocus = () => {
        refreshCategories();
      };

      window.addEventListener("focus", handleFocus);
      return () => window.removeEventListener("focus", handleFocus);
    }
  }, [refreshOnFocus, refreshCategories]);

  return {
    categories,
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    refreshCategories,
    clearError,
  };
};

export default useChecklistCategories;
